import * as React from "react";
import ReactDOM from "react-dom";
import { isFunction, defer, bind } from "underscore";

import { getRootElement } from "util/fullscreen-utils";
import { EVENTS } from "const/constants";

import Icon from "../decorations/icons/icon";

import {
    ToggleMenuButton,
    List,
    Item,
    SectionedItemsContainer
} from "./context-menu.styles";

export type DropdownSection = {
    id: string;
    items: DropdownSectionItem[];
};

export type DropdownSectionItem = {
    id: string;
    text: string;
    icon?: string;
    onClick: React.MouseEventHandler;
};

const onItemClick = (
    evt: React.MouseEvent,
    { onClick }: { onClick: React.MouseEventHandler }
) => {
    if (isFunction(onClick)) {
        evt.preventDefault();
        onClick(evt);
    }
};

interface DropdownItemProps {
    id: string;
    disabled?: boolean;
    href?: string;
    target?: string;
    text: string;
    icon?: string;
    onClick: React.MouseEventHandler;
    color?: string;
    onItemClicked: React.MouseEventHandler;
}

const DropdownItem = (props: DropdownItemProps) => {
    const {
        id,
        disabled,
        href,
        icon,
        target,
        text,
        onClick,
        color,
        onItemClicked
    } = props;

    const handleItemClick: React.MouseEventHandler = (evt) => {
        if (!disabled) {
            onItemClick(evt, { onClick });
        }
        onItemClicked && onItemClicked(evt);
    };

    return (
        <Item
            id={id}
            deleteOption={id === `delete`}
            disabled={disabled}
            color={color}
        >
            {icon && <Icon size="30px" name={icon} />}
            {href ? (
                <a
                    role="link"
                    href={href}
                    target={target || `_blank`}
                    onClick={handleItemClick}
                >
                    {text}
                </a>
            ) : (
                <a role="link" onClick={handleItemClick}>
                    {text}
                </a>
            )}
        </Item>
    );
};

interface ContextMenuButtonProps {
    dropdownOptions: DropdownSectionItem[];
    dataTestId?: string;
    isRound?: boolean;
    iconSize?: string;
    CustomMenuButton?: React.ComponentType;
}

export const ContextMenuButton = (props: ContextMenuButtonProps) => {
    const { dropdownOptions, dataTestId, isRound, iconSize, CustomMenuButton } =
        props;

    const [isMenuOpen, toggleContextMenu] = React.useState(false);
    const [menuPositions, setMenuPosition] = React.useState(null);
    const dropdownRef = React.useRef<HTMLUListElement>();

    const showContextMenu = () => (
        <Dropdown
            dropdownRef={dropdownRef}
            items={dropdownOptions}
            positionX={menuPositions.x || 0}
            positionY={menuPositions.y || 0}
            onClose={() => dropdownRef.current && toggleContextMenu(false)}
        />
    );

    React.useLayoutEffect(() => {
        if (isMenuOpen && dropdownRef.current) {
            const width = dropdownRef.current.offsetWidth;
            if (menuPositions.x + width > window.innerWidth) {
                setMenuPosition({
                    x: menuPositions.x - width,
                    y: menuPositions.y
                });
            }
        }
    }, [isMenuOpen]);

    const handleContextMenu = (event) => {
        toggleContextMenu(!isMenuOpen);
        setMenuPosition({ x: event.pageX, y: event.pageY });
    };

    const onClick = (evt) => {
        handleContextMenu(evt);
    };

    return (
        <>
            <ToggleMenuButton
                type="button"
                isRound={isRound}
                data-testid={dataTestId}
                onClick={onClick}
            >
                {CustomMenuButton ? (
                    <CustomMenuButton />
                ) : (
                    <Icon size={iconSize} name="small-menu" />
                )}
            </ToggleMenuButton>
            {isMenuOpen && showContextMenu()}
        </>
    );
};

export type DropdownAlignment = "left" | "right";

interface DropdownProps {
    dropdownRef?: React.RefObject<HTMLUListElement>;
    onOpen?: () => void;
    onClose: React.MouseEventHandler;
    parentRef?: React.RefObject<HTMLElement>;
    offsetX?: number;
    positionX: number;
    positionY: number;
    alignX?: DropdownAlignment;
    className?: string;
    sectionedItems?: DropdownSection[];
    items?: DropdownSectionItem[];
}

class Dropdown extends React.Component<
    DropdownProps,
    {
        componentWidth: number | null;
        componentHeight: number | null;
    }
> {
    ref: React.RefObject<HTMLUListElement>;

    constructor(props: DropdownProps) {
        super(props);
        this.ref = props.dropdownRef || React.createRef();
        this.state = {
            componentWidth: null,
            componentHeight: null
        };
    }

    componentDidMount() {
        const { onOpen } = this.props;
        defer(
            bind(() => {
                //@ts-ignore: mismatch between event types
                window.addEventListener(EVENTS.CLICK, this.onClickAnywhere);
                //@ts-ignore: mismatch between event types
                window.addEventListener(
                    EVENTS.CONTEXT_MENU,
                    this.onClickAnywhere
                );
            }, this)
        );

        this.setState({
            componentWidth: this.ref.current.getBoundingClientRect().width,
            componentHeight: this.ref.current.getBoundingClientRect().height
        });

        onOpen && onOpen();
    }

    componentWillUnmount() {
        //@ts-ignore: mismatch between event types
        window.removeEventListener(EVENTS.CLICK, this.onClickAnywhere);
        //@ts-ignore: mismatch between event types
        window.removeEventListener(EVENTS.CONTEXT_MENU, this.onClickAnywhere);
    }

    onItemClicked = (e: React.MouseEvent) => {
        this.close(e);
    };

    onClickAnywhere = (e: React.MouseEvent) => {
        this.close(e);
    };

    close = (e: React.MouseEvent) => {
        this.props.onClose(e);
    };

    render() {
        const { parentRef, offsetX = 5 } = this.props;
        let { positionX, positionY, alignX } = this.props;
        const { componentWidth, componentHeight } = this.state;
        const parentRefCoords =
            parentRef && parentRef.current.getBoundingClientRect();
        const spaceFromBrowserBottom =
            parentRefCoords &&
            window.innerHeight - (parentRefCoords.top + parentRefCoords.height);
        const spaceFromBrowserRight =
            parentRefCoords &&
            window.innerWidth - (parentRefCoords.width + parentRefCoords.x);
        if (spaceFromBrowserBottom < componentHeight) {
            positionY = spaceFromBrowserBottom;
        }
        if (alignX) {
            if (alignX === `left` && parentRefCoords.x < componentWidth) {
                alignX = `right`;
            }
            if (alignX === `right` && spaceFromBrowserRight < componentWidth) {
                alignX = `left`;
            }
            switch (alignX) {
                case `right`:
                    positionX =
                        parentRefCoords.width + parentRefCoords.x + offsetX;
                    break;
                default:
                    positionX = parentRefCoords.x - componentWidth - offsetX;
            }
        }

        return ReactDOM.createPortal(
            <List
                ref={this.ref}
                positionX={positionX}
                positionY={positionY}
                className={this.props.className}
                renderAboveViewport={spaceFromBrowserBottom < componentHeight}
                onClick={(e) => {
                    e?.stopPropagation();
                }}
            >
                {this.props.items &&
                    this.props.items
                        .filter((item) => !!item)
                        .map((item) => (
                            <DropdownItem
                                key={item.id}
                                {...item}
                                onItemClicked={this.onItemClicked}
                                {...this.props}
                            />
                        ))}
                {this.props.sectionedItems &&
                    this.props.sectionedItems.map((section) => (
                        <SectionedItemsContainer key={section.id}>
                            <ul>
                                {section.items.map((item) => (
                                    <DropdownItem
                                        key={item.id}
                                        {...item}
                                        onItemClicked={this.onItemClicked}
                                        {...this.props}
                                    />
                                ))}
                            </ul>
                        </SectionedItemsContainer>
                    ))}
            </List>,
            getRootElement()
        );
    }
}

export default Dropdown;
